<!-- Backdrop -->
<div
  class="fixed inset-0 z-40 bg-black/30 transition-opacity duration-200"
  [class.opacity-0]="!open"
  [class.pointer-events-none]="!open"
  (click)="requestClose()"
  aria-hidden="true"
></div>

<!-- Drawer panel -->
<div
  class="fixed bottom-0 right-0 top-0 z-50 flex w-[480px] max-w-full flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out"
  [class.translate-x-full]="!open"
  role="dialog"
  [attr.aria-label]="(service?.name ?? 'Service') + ' settings'"
  [attr.aria-modal]="open"
>

  <!-- ── Header ─────────────────────────────────────────────────────────────── -->
  <div class="flex flex-shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4">
    <div class="flex items-center gap-3">
      <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
        <lucide-icon [name]="service?.icon ?? 'settings'" [size]="15" class="text-slate-600" [strokeWidth]="1.75"></lucide-icon>
      </div>
      <div>
        <p class="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Service Configuration</p>
        <h2 class="text-sm font-semibold text-slate-900">{{ service?.name }}</h2>
      </div>
    </div>
    <button
      type="button"
      (click)="requestClose()"
      class="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-300"
      aria-label="Close"
    >
      <lucide-icon name="x" [size]="15" [strokeWidth]="2"></lucide-icon>
    </button>
  </div>

  <!-- ── Discard-changes confirmation banner ────────────────────────────────── -->
  <div
    *ngIf="showDiscardConfirm"
    class="flex-shrink-0 border-b border-amber-200 bg-amber-50 px-5 py-4"
    role="alert"
  >
    <p class="text-sm font-semibold text-amber-900">Discard unsaved changes?</p>
    <p class="mt-0.5 text-xs text-amber-700">Your edits will be lost if you close without saving.</p>
    <div class="mt-3 flex gap-2">
      <button
        type="button"
        (click)="confirmDiscard()"
        class="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-400"
      >Discard changes</button>
      <button
        type="button"
        (click)="cancelDiscard()"
        class="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-300"
      >Keep editing</button>
    </div>
  </div>

  <!-- ── Body ───────────────────────────────────────────────────────────────── -->
  <div class="flex-1 overflow-y-auto" *ngIf="draft">

    <!-- Empty state -->
    <div *ngIf="!hasAnyEnabled" class="px-5 pt-8 pb-4 text-center">
      <div class="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
        <lucide-icon name="bell" [size]="18" class="text-slate-400" [strokeWidth]="1.5"></lucide-icon>
      </div>
      <p class="text-sm font-medium text-slate-500">No alerts or ticketing configured</p>
      <p class="mt-1 text-xs text-slate-400">Enable a channel below to start receiving notifications and creating tickets when this service fails.</p>
    </div>

    <!-- ── Notification Channels ─────────────────────────────────────────────── -->
    <section class="px-5 py-5">
      <div class="mb-4 flex items-center gap-2">
        <lucide-icon name="bell" [size]="13" class="text-slate-500" [strokeWidth]="2"></lucide-icon>
        <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-500">Notification Channels</h3>
      </div>

      <!-- ── Slack ──────────────────────────────────────────────────────────── -->
      <div class="mb-3 overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-2.5">
            <lucide-icon name="message-square" [size]="14" class="text-slate-500" [strokeWidth]="2"></lucide-icon>
            <span class="text-sm font-medium text-slate-900">Slack</span>
          </div>
          <button
            type="button" role="switch"
            [attr.aria-checked]="draft.notifications.slack.enabled"
            (click)="draft.notifications.slack.enabled = !draft.notifications.slack.enabled"
            class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:ring-offset-1"
            [class.bg-violet-600]="draft.notifications.slack.enabled"
            [class.bg-slate-200]="!draft.notifications.slack.enabled"
          >
            <span aria-hidden="true"
              class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200"
              [class.translate-x-4]="draft.notifications.slack.enabled"
              [class.translate-x-0]="!draft.notifications.slack.enabled"></span>
          </button>
        </div>
        <div *ngIf="draft.notifications.slack.enabled" class="border-t border-slate-100 px-4 pb-4 pt-3 space-y-3">
          <!-- Destination mode selector -->
          <div>
            <p class="mb-1.5 text-xs font-medium text-slate-600">Destination</p>
            <div class="flex overflow-hidden rounded-md border border-slate-200 text-xs">
              <button type="button"
                (click)="setConfigMode('notifications', 'slack', 'inherited')"
                class="flex-1 px-3 py-1.5 font-medium transition-colors focus:outline-none"
                [class.bg-slate-100]="draft.notifications.slack.configMode === 'inherited'"
                [class.text-slate-800]="draft.notifications.slack.configMode === 'inherited'"
                [class.text-slate-400]="draft.notifications.slack.configMode !== 'inherited'"
              >Use organization default</button>
              <div class="w-px bg-slate-200"></div>
              <button type="button"
                (click)="setConfigMode('notifications', 'slack', 'custom')"
                class="flex-1 px-3 py-1.5 font-medium transition-colors focus:outline-none"
                [class.bg-violet-600]="draft.notifications.slack.configMode === 'custom'"
                [class.text-white]="draft.notifications.slack.configMode === 'custom'"
                [class.text-slate-400]="draft.notifications.slack.configMode !== 'custom'"
              >Use custom destination</button>
            </div>
          </div>
          <!-- Inherited: show org summary or missing warning -->
          <div *ngIf="draft.notifications.slack.configMode === 'inherited'">
            <div *ngIf="hasOrgSlack" class="flex items-center gap-1.5 text-xs text-slate-500">
              <lucide-icon name="circle-check" [size]="12" class="text-emerald-500" [strokeWidth]="2"></lucide-icon>
              <span>Using organization default: <span class="font-mono text-slate-700">{{ orgSlackSummary() }}</span></span>
            </div>
            <div *ngIf="!hasOrgSlack" class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              <span class="font-medium">No organization default configured.</span>
              Go to <a routerLink="/settings/integrations" class="underline hover:text-amber-900">Settings → Integrations</a> to set one, or use a custom destination.
            </div>
          </div>
          <!-- Custom: URL input -->
          <div *ngIf="draft.notifications.slack.configMode === 'custom'">
            <label class="mb-1.5 block text-xs font-medium text-slate-700">Webhook URL <span class="text-red-500">*</span></label>
            <input
              type="url"
              [(ngModel)]="draft.notifications.slack.overrideConfig!.webhookUrl"
              [ngModelOptions]="{standalone: true}"
              placeholder="https://hooks.slack.com/services/…"
              class="w-full rounded-md border px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1"
              [class.border-slate-200]="!slackUrlError" [class.focus:border-violet-400]="!slackUrlError" [class.focus:ring-violet-300]="!slackUrlError"
              [class.border-red-300]="slackUrlError"   [class.focus:border-red-400]="slackUrlError"     [class.focus:ring-red-300]="slackUrlError"
              (ngModelChange)="draft.notifications.slack.overrideConfig = { webhookUrl: $event }"
            >
            <p *ngIf="slackUrlError" class="mt-1.5 text-xs text-red-500">Enter a valid https:// webhook URL.</p>
          </div>
        </div>
      </div>

      <!-- ── Microsoft Teams ────────────────────────────────────────────────── -->
      <div class="mb-3 overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-2.5">
            <lucide-icon name="users" [size]="14" class="text-slate-500" [strokeWidth]="2"></lucide-icon>
            <span class="text-sm font-medium text-slate-900">Microsoft Teams</span>
          </div>
          <button
            type="button" role="switch"
            [attr.aria-checked]="draft.notifications.teams.enabled"
            (click)="draft.notifications.teams.enabled = !draft.notifications.teams.enabled"
            class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:ring-offset-1"
            [class.bg-violet-600]="draft.notifications.teams.enabled"
            [class.bg-slate-200]="!draft.notifications.teams.enabled"
          >
            <span aria-hidden="true"
              class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200"
              [class.translate-x-4]="draft.notifications.teams.enabled"
              [class.translate-x-0]="!draft.notifications.teams.enabled"></span>
          </button>
        </div>
        <div *ngIf="draft.notifications.teams.enabled" class="border-t border-slate-100 px-4 pb-4 pt-3 space-y-3">
          <div>
            <p class="mb-1.5 text-xs font-medium text-slate-600">Destination</p>
            <div class="flex overflow-hidden rounded-md border border-slate-200 text-xs">
              <button type="button"
                (click)="setConfigMode('notifications', 'teams', 'inherited')"
                class="flex-1 px-3 py-1.5 font-medium transition-colors focus:outline-none"
                [class.bg-slate-100]="draft.notifications.teams.configMode === 'inherited'"
                [class.text-slate-800]="draft.notifications.teams.configMode === 'inherited'"
                [class.text-slate-400]="draft.notifications.teams.configMode !== 'inherited'"
              >Use organization default</button>
              <div class="w-px bg-slate-200"></div>
              <button type="button"
                (click)="setConfigMode('notifications', 'teams', 'custom')"
                class="flex-1 px-3 py-1.5 font-medium transition-colors focus:outline-none"
                [class.bg-violet-600]="draft.notifications.teams.configMode === 'custom'"
                [class.text-white]="draft.notifications.teams.configMode === 'custom'"
                [class.text-slate-400]="draft.notifications.teams.configMode !== 'custom'"
              >Use custom destination</button>
            </div>
          </div>
          <div *ngIf="draft.notifications.teams.configMode === 'inherited'">
            <div *ngIf="hasOrgTeams" class="flex items-center gap-1.5 text-xs text-slate-500">
              <lucide-icon name="circle-check" [size]="12" class="text-emerald-500" [strokeWidth]="2"></lucide-icon>
              <span>Using organization default: <span class="font-mono text-slate-700">{{ orgTeamsSummary() }}</span></span>
            </div>
            <div *ngIf="!hasOrgTeams" class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              <span class="font-medium">No organization default configured.</span>
              Go to <a routerLink="/settings/integrations" class="underline hover:text-amber-900">Settings → Integrations</a> to set one, or use a custom destination.
            </div>
          </div>
          <div *ngIf="draft.notifications.teams.configMode === 'custom'">
            <label class="mb-1.5 block text-xs font-medium text-slate-700">Webhook URL <span class="text-red-500">*</span></label>
            <input
              type="url"
              [(ngModel)]="draft.notifications.teams.overrideConfig!.webhookUrl"
              [ngModelOptions]="{standalone: true}"
              placeholder="https://outlook.office.com/webhook/…"
              class="w-full rounded-md border px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1"
              [class.border-slate-200]="!teamsUrlError" [class.focus:border-violet-400]="!teamsUrlError" [class.focus:ring-violet-300]="!teamsUrlError"
              [class.border-red-300]="teamsUrlError"   [class.focus:border-red-400]="teamsUrlError"     [class.focus:ring-red-300]="teamsUrlError"
              (ngModelChange)="draft.notifications.teams.overrideConfig = { webhookUrl: $event }"
            >
            <p *ngIf="teamsUrlError" class="mt-1.5 text-xs text-red-500">Enter a valid https:// webhook URL.</p>
          </div>
        </div>
      </div>

      <!-- ── Email ──────────────────────────────────────────────────────────── -->
      <div class="rounded-lg border border-slate-200 bg-white">
        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-2.5">
            <lucide-icon name="mail" [size]="14" class="text-slate-500" [strokeWidth]="2"></lucide-icon>
            <span class="text-sm font-medium text-slate-900">Email</span>
          </div>
          <button
            type="button" role="switch"
            [attr.aria-checked]="draft.notifications.email.enabled"
            (click)="draft.notifications.email.enabled = !draft.notifications.email.enabled"
            class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:ring-offset-1"
            [class.bg-violet-600]="draft.notifications.email.enabled"
            [class.bg-slate-200]="!draft.notifications.email.enabled"
          >
            <span aria-hidden="true"
              class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200"
              [class.translate-x-4]="draft.notifications.email.enabled"
              [class.translate-x-0]="!draft.notifications.email.enabled"></span>
          </button>
        </div>
        <div *ngIf="draft.notifications.email.enabled" class="border-t border-slate-100 px-4 pb-4 pt-3 space-y-3">
          <div>
            <p class="mb-1.5 text-xs font-medium text-slate-600">Destination</p>
            <div class="flex overflow-hidden rounded-md border border-slate-200 text-xs">
              <button type="button"
                (click)="setConfigMode('notifications', 'email', 'inherited')"
                class="flex-1 px-3 py-1.5 font-medium transition-colors focus:outline-none"
                [class.bg-slate-100]="draft.notifications.email.configMode === 'inherited'"
                [class.text-slate-800]="draft.notifications.email.configMode === 'inherited'"
                [class.text-slate-400]="draft.notifications.email.configMode !== 'inherited'"
              >Use organization default</button>
              <div class="w-px bg-slate-200"></div>
              <button type="button"
                (click)="setConfigMode('notifications', 'email', 'custom')"
                class="flex-1 px-3 py-1.5 font-medium transition-colors focus:outline-none"
                [class.bg-violet-600]="draft.notifications.email.configMode === 'custom'"
                [class.text-white]="draft.notifications.email.configMode === 'custom'"
                [class.text-slate-400]="draft.notifications.email.configMode !== 'custom'"
              >Use custom destination</button>
            </div>
          </div>
          <div *ngIf="draft.notifications.email.configMode === 'inherited'">
            <div *ngIf="hasOrgEmail" class="flex items-center gap-1.5 text-xs text-slate-500">
              <lucide-icon name="circle-check" [size]="12" class="text-emerald-500" [strokeWidth]="2"></lucide-icon>
              <span>Using organization default: <span class="text-slate-700">{{ orgEmailSummary() }}</span></span>
            </div>
            <div *ngIf="!hasOrgEmail" class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              <span class="font-medium">No organization default configured.</span>
              Go to <a routerLink="/settings/integrations" class="underline hover:text-amber-900">Settings → Integrations</a> to set one, or use a custom destination.
            </div>
          </div>
          <div *ngIf="draft.notifications.email.configMode === 'custom'">
            <label class="mb-1.5 block text-xs font-medium text-slate-700">Recipients <span class="text-red-500">*</span></label>
            <app-member-picker
              [members]="orgMembers"
              [selectedIds]="draft.notifications.email.overrideConfig?.recipientIds ?? []"
              (selectedIdsChange)="draft.notifications.email.overrideConfig = { recipientIds: $event }"
              [readOnly]="false"
            ></app-member-picker>
            <p *ngIf="emailError" class="mt-1 text-xs text-red-500">At least one recipient is required.</p>
          </div>
        </div>
      </div>
    </section>

    <div class="mx-5 border-t border-slate-100"></div>

    <!-- ── Ticketing Integrations ─────────────────────────────────────────────── -->
    <section class="px-5 py-5">
      <div class="mb-4 flex items-center gap-2">
        <lucide-icon name="tag" [size]="13" class="text-slate-500" [strokeWidth]="2"></lucide-icon>
        <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-500">Ticketing Integrations</h3>
      </div>

      <!-- ── GitHub Issues ──────────────────────────────────────────────────── -->
      <div class="mb-3 overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-2.5">
            <lucide-icon name="github" [size]="14" class="text-slate-500" [strokeWidth]="2"></lucide-icon>
            <span class="text-sm font-medium text-slate-900">GitHub Issues</span>
          </div>
          <button
            type="button" role="switch"
            [attr.aria-checked]="draft.ticketing.github.enabled"
            (click)="draft.ticketing.github.enabled = !draft.ticketing.github.enabled"
            class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:ring-offset-1"
            [class.bg-violet-600]="draft.ticketing.github.enabled"
            [class.bg-slate-200]="!draft.ticketing.github.enabled"
          >
            <span aria-hidden="true"
              class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200"
              [class.translate-x-4]="draft.ticketing.github.enabled"
              [class.translate-x-0]="!draft.ticketing.github.enabled"></span>
          </button>
        </div>
        <div *ngIf="draft.ticketing.github.enabled" class="border-t border-slate-100 px-4 pb-4 pt-3 space-y-3">
          <div>
            <p class="mb-1.5 text-xs font-medium text-slate-600">Destination</p>
            <div class="flex overflow-hidden rounded-md border border-slate-200 text-xs">
              <button type="button"
                (click)="setConfigMode('ticketing', 'github', 'inherited')"
                class="flex-1 px-3 py-1.5 font-medium transition-colors focus:outline-none"
                [class.bg-slate-100]="draft.ticketing.github.configMode === 'inherited'"
                [class.text-slate-800]="draft.ticketing.github.configMode === 'inherited'"
                [class.text-slate-400]="draft.ticketing.github.configMode !== 'inherited'"
              >Use organization default</button>
              <div class="w-px bg-slate-200"></div>
              <button type="button"
                (click)="setConfigMode('ticketing', 'github', 'custom')"
                class="flex-1 px-3 py-1.5 font-medium transition-colors focus:outline-none"
                [class.bg-violet-600]="draft.ticketing.github.configMode === 'custom'"
                [class.text-white]="draft.ticketing.github.configMode === 'custom'"
                [class.text-slate-400]="draft.ticketing.github.configMode !== 'custom'"
              >Use custom destination</button>
            </div>
          </div>
          <div *ngIf="draft.ticketing.github.configMode === 'inherited'">
            <div *ngIf="hasOrgGitHub" class="flex items-center gap-1.5 text-xs text-slate-500">
              <lucide-icon name="circle-check" [size]="12" class="text-emerald-500" [strokeWidth]="2"></lucide-icon>
              <span>Using organization default: <span class="font-mono text-slate-700">{{ orgGitHubSummary() }}</span></span>
            </div>
            <div *ngIf="!hasOrgGitHub" class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              <span class="font-medium">No organization default configured.</span>
              Connect a GitHub repository in <a routerLink="/settings/integrations" class="underline hover:text-amber-900">Settings → Integrations</a>.
            </div>
          </div>
          <div *ngIf="draft.ticketing.github.configMode === 'custom'">
            <label class="mb-1.5 block text-xs font-medium text-slate-700">Repository <span class="text-red-500">*</span></label>
            <input
              type="text"
              [(ngModel)]="draft.ticketing.github.overrideConfig!.repository"
              [ngModelOptions]="{standalone: true}"
              placeholder="owner/repo-name"
              class="w-full rounded-md border px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1"
              [class.border-slate-200]="!githubError" [class.focus:border-violet-400]="!githubError" [class.focus:ring-violet-300]="!githubError"
              [class.border-red-300]="githubError"   [class.focus:border-red-400]="githubError"     [class.focus:ring-red-300]="githubError"
              (ngModelChange)="draft.ticketing.github.overrideConfig = { repository: $event }"
            >
            <p *ngIf="githubError" class="mt-1.5 text-xs text-red-500">Use the format <span class="font-mono">owner/repository</span>.</p>
          </div>
        </div>
      </div>

      <!-- ── Jira ───────────────────────────────────────────────────────────── -->
      <div class="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-2.5">
            <div class="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-sm bg-blue-600">
              <span class="text-[9px] font-bold leading-none text-white">J</span>
            </div>
            <span class="text-sm font-medium text-slate-900">Jira</span>
          </div>
          <button
            type="button" role="switch"
            [attr.aria-checked]="draft.ticketing.jira.enabled"
            (click)="draft.ticketing.jira.enabled = !draft.ticketing.jira.enabled"
            class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:ring-offset-1"
            [class.bg-violet-600]="draft.ticketing.jira.enabled"
            [class.bg-slate-200]="!draft.ticketing.jira.enabled"
          >
            <span aria-hidden="true"
              class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200"
              [class.translate-x-4]="draft.ticketing.jira.enabled"
              [class.translate-x-0]="!draft.ticketing.jira.enabled"></span>
          </button>
        </div>
        <div *ngIf="draft.ticketing.jira.enabled" class="border-t border-slate-100 px-4 pb-4 pt-3 space-y-3">
          <div>
            <p class="mb-1.5 text-xs font-medium text-slate-600">Destination</p>
            <div class="flex overflow-hidden rounded-md border border-slate-200 text-xs">
              <button type="button"
                (click)="setConfigMode('ticketing', 'jira', 'inherited')"
                class="flex-1 px-3 py-1.5 font-medium transition-colors focus:outline-none"
                [class.bg-slate-100]="draft.ticketing.jira.configMode === 'inherited'"
                [class.text-slate-800]="draft.ticketing.jira.configMode === 'inherited'"
                [class.text-slate-400]="draft.ticketing.jira.configMode !== 'inherited'"
              >Use organization default</button>
              <div class="w-px bg-slate-200"></div>
              <button type="button"
                (click)="setConfigMode('ticketing', 'jira', 'custom')"
                class="flex-1 px-3 py-1.5 font-medium transition-colors focus:outline-none"
                [class.bg-violet-600]="draft.ticketing.jira.configMode === 'custom'"
                [class.text-white]="draft.ticketing.jira.configMode === 'custom'"
                [class.text-slate-400]="draft.ticketing.jira.configMode !== 'custom'"
              >Use custom destination</button>
            </div>
          </div>
          <div *ngIf="draft.ticketing.jira.configMode === 'inherited'">
            <div *ngIf="hasOrgJira" class="flex items-center gap-1.5 text-xs text-slate-500">
              <lucide-icon name="circle-check" [size]="12" class="text-emerald-500" [strokeWidth]="2"></lucide-icon>
              <span>Using organization default: project key <span class="font-mono text-slate-700">{{ orgJiraSummary() }}</span></span>
            </div>
            <div *ngIf="!hasOrgJira" class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              <span class="font-medium">No organization default configured.</span>
              Connect Jira in <a routerLink="/settings/integrations" class="underline hover:text-amber-900">Settings → Integrations</a>.
            </div>
          </div>
          <div *ngIf="draft.ticketing.jira.configMode === 'custom'">
            <label class="mb-1.5 block text-xs font-medium text-slate-700">Project Key <span class="text-red-500">*</span></label>
            <input
              type="text"
              [(ngModel)]="draft.ticketing.jira.overrideConfig!.projectKey"
              [ngModelOptions]="{standalone: true}"
              placeholder="OPS"
              class="w-full rounded-md border px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1"
              [class.border-slate-200]="!jiraError" [class.focus:border-violet-400]="!jiraError" [class.focus:ring-violet-300]="!jiraError"
              [class.border-red-300]="jiraError"   [class.focus:border-red-400]="jiraError"     [class.focus:ring-red-300]="jiraError"
              (ngModelChange)="draft.ticketing.jira.overrideConfig = { projectKey: $event }"
            >
            <p class="mt-1.5 text-xs text-slate-400">The Jira project key (e.g. OPS, INFRA, DATA).</p>
            <p *ngIf="jiraError" class="mt-1 text-xs text-red-500">Project key is required.</p>
          </div>
        </div>
      </div>
    </section>

    <div class="h-4"></div>
  </div>

  <!-- ── Footer ─────────────────────────────────────────────────────────────── -->
  <div class="flex flex-shrink-0 items-center justify-end gap-3 border-t border-slate-200 bg-white px-5 py-4">
    <button
      type="button"
      (click)="requestClose()"
      [disabled]="saveState === 'saving'"
      class="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:cursor-not-allowed disabled:opacity-50"
    >Cancel</button>
    <button
      type="button"
      (click)="save()"
      [disabled]="saveState === 'saving'"
      class="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <lucide-icon *ngIf="saveState === 'saving'" name="loader" [size]="13" [strokeWidth]="2" class="animate-spin"></lucide-icon>
      {{ saveState === 'saving' ? 'Saving…' : 'Save' }}
    </button>
  </div>

</div>
